import { Suspense, useEffect } from 'react';
import { ButtonLink, LayerBoundary, Layers, NavProvider, PageBoundary, PlaceProvider, Reconnecting, Screen, StaffBar, StaffLayout, composeRoutes, currentStaff, hostGroup, navigate, readStaffSession, strings, toSignIn, usePlace, useStaffSession, type OldLink, type Place, type RouteMatch, type StaffSession } from './kit/index.ts';
import { uiParts } from './features.gen.ts';
import { SignInPage } from './sign-in.tsx';

/**
 * The command center shell: its own routes (sign-in, the `/` redirect, not
 * found) and every feature's pages, from the generated features.gen.ts.
 * Adding a page never edits this file; `/` and sign-in send staff to the one
 * route a feature marks `staffHome`, and the staff frame's sidebar lists the
 * pages the features put in it (bindPages' NavDecl). Over every page, the
 * features' layers (bindPages' LayerDecl), for staff they show to, and the
 * kit's reconnecting line while a call waits to retry. Every page sits in
 * the kit's PageBoundary: one that throws shows "Something went wrong" with
 * Retry, never a blank page; a layer that fails draws nothing.
 *
 * The staff pages share one StaffLayout: moving between them swaps the page
 * under the frame, never the frame, and never waits on the session, which
 * is read once and asked again in the background on every move. A move is
 * a transition (PlaceProvider): the page moved from stays until the next is
 * ready, and the frame shows the page's skeleton only if that is slow.
 *
 * A staff page's URL names no group: the shell reads the session first and
 * hands the page the signed-in staff member's group. An old staff link,
 * `/g/<group>/<path>`, opens `<path>` for staff of that group and is refused
 * for anyone else; the cloud refuses another group's id on every API call
 * too (cloud/src/auth/actor.ts).
 */
const routes = composeRoutes(uiParts);

export function App() {
  return (
    <PlaceProvider holds={staffToStaff}>
      <Route />
      <LayerBoundary>
        <Layers layers={routes.layers} />
      </LayerBoundary>
      <Reconnecting />
    </PlaceProvider>
  );
}

/** The moves that keep the page moved from on screen until the next is ready: from one staff page to another, under the one StaffLayout. */
function staffToStaff(from: Place, to: Place): boolean {
  return routes.match(from.path)?.route.access === 'staff' && routes.match(to.path)?.route.access === 'staff';
}

function Route() {
  const { path, query } = usePlace();
  const found = routes.match(path);
  if (found !== undefined) {
    return (
      <NavProvider entries={routes.nav}>
        {found.route.access === 'staff' ? (
          <StaffPage found={found} query={query} />
        ) : (
          <PageBoundary key={found.route.path} kind="guest">
            <Suspense fallback={null}>
              <found.Page params={found.params} query={query} />
            </Suspense>
          </PageBoundary>
        )}
      </NavProvider>
    );
  }
  // A group's own host shows its public pages only: staff sign in on the product's host.
  if (hostGroup() !== null) {
    if (path === '/' && routes.hostHome !== undefined) return <HostHome to={routes.hostHome} />;
  } else {
    const old = routes.oldLink(path);
    if (old !== undefined) return <OldStaffLink link={old} query={query} />;
    if (path === '/sign-in') return <SignInPage staffHome={routes.staffHome} />;
    if (path === '/') return <Home />;
  }
  return (
    <Screen kind="office" title={strings.common.notFound} bar={<StaffBar />}>
      {null}
    </Screen>
  );
}

function HostHome(props: { readonly to: string }) {
  useEffect(() => {
    navigate(props.to + location.search, { replace: true });
  }, [props.to]);
  return null;
}

function Home() {
  useEffect(() => {
    currentStaff()
      .then((staff) => {
        navigate(staff === null ? '/sign-in' : routes.staffHome, { replace: true });
      })
      .catch(() => {
        navigate('/sign-in', { replace: true });
      });
  }, []);
  return null;
}

/** The signed-in staff member's session, held across pages and asked again in the background on every move; signed out goes to sign-in, coming back here after. */
function useSession(): StaffSession {
  const session = useStaffSession();
  const { path } = usePlace();
  useEffect(() => {
    void readStaffSession();
  }, [path]);
  const signedOut = session.state === 'signed out';
  useEffect(() => {
    if (signedOut) toSignIn();
  }, [signedOut]);
  return session;
}

function StaffPage({ found, query }: { readonly found: RouteMatch; readonly query: URLSearchParams }) {
  const session = useSession();
  if (session.state === 'offline') return <OfflineScreen />;
  if (session.state !== 'signed in') return null;
  return (
    <StaffLayout who={session.staff} onShown={routes.preloadStaffPages}>
      <PageBoundary key={found.route.path} kind="office">
        <found.Page params={found.params} query={query} groupId={session.staff.groupId} />
      </PageBoundary>
    </StaffLayout>
  );
}

function OldStaffLink({ link, query }: { readonly link: OldLink; readonly query: URLSearchParams }) {
  const session = useSession();
  const own = session.state === 'signed in' && session.staff.groupId.toLowerCase() === link.groupId;
  const search = query.toString();
  useEffect(() => {
    if (own) navigate(search === '' ? link.path : `${link.path}?${search}`, { replace: true });
  }, [own, link.path, search]);
  if (session.state === 'offline') return <OfflineScreen />;
  if (session.state !== 'signed in' || own) return null;
  return (
    <Screen kind="office" title={strings.staff.otherGroup} lead={strings.staff.otherGroupBody} bar={<StaffBar />}>
      <ButtonLink href={routes.staffHome}>{strings.staff.toOwnGroup}</ButtonLink>
    </Screen>
  );
}

function OfflineScreen() {
  return (
    <Screen kind="office" title={strings.common.offline} bar={<StaffBar />}>
      {null}
    </Screen>
  );
}
